import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Trash2, Send, Mail } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface Sub {
  id: string;
  recipient_email: string;
  recipient_name: string | null;
  region: string | null;
  frequency: "daily" | "weekly";
  day_of_week: number | null;
  hour_of_day: number;
  timezone: string;
  include_excel: boolean;
  is_active: boolean;
  last_sent_at: string | null;
  last_error: string | null;
}

interface Props {
  userRole?: string;
  regionId?: string | null;
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export const ScheduledReportsManager = ({ userRole, regionId }: Props) => {
  const isGlobal = userRole === "global_admin" || userRole === "president";
  const [subs, setSubs] = useState<Sub[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [myRegionName, setMyRegionName] = useState<string | null>(null);
  const [form, setForm] = useState({
    recipient_email: "",
    recipient_name: "",
    region: "",
    frequency: "weekly" as "daily" | "weekly",
    day_of_week: 1,
    hour_of_day: 8,
    timezone: "America/New_York",
  });
  const [regions, setRegions] = useState<{ id: string; name: string }[]>([]);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("scheduled_report_subscriptions" as any)
      .select("*")
      .order("created_at", { ascending: false });
    setSubs((data as any) || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    if (isGlobal) {
      supabase.from("regions").select("id,name").order("name").then(({ data }) => {
        setRegions((data as any) || []);
      });
    } else if (regionId) {
      supabase.from("regions").select("name").eq("id", regionId).single().then(({ data }) => {
        if (data) setMyRegionName((data as any).name);
      });
    }
  }, [isGlobal, regionId]);

  const add = async () => {
    if (!form.recipient_email.trim()) {
      toast({ title: "Email required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload: any = {
      recipient_email: form.recipient_email.trim(),
      recipient_name: form.recipient_name.trim() || null,
      region: isGlobal ? (form.region || null) : myRegionName,
      frequency: form.frequency,
      day_of_week: form.frequency === "weekly" ? form.day_of_week : null,
      hour_of_day: form.hour_of_day,
      timezone: form.timezone,
    };
    const { error } = await supabase.from("scheduled_report_subscriptions" as any).insert(payload);
    setSaving(false);
    if (error) {
      toast({ title: "Could not add", description: error.message, variant: "destructive" });
      return;
    }
    setForm({ ...form, recipient_email: "", recipient_name: "" });
    toast({ title: "Subscription added" });
    load();
  };

  const toggle = async (id: string, is_active: boolean) => {
    await supabase.from("scheduled_report_subscriptions" as any).update({ is_active }).eq("id", id);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this subscription?")) return;
    await supabase.from("scheduled_report_subscriptions" as any).delete().eq("id", id);
    load();
  };

  const sendNow = async (id: string) => {
    setTestingId(id);
    try {
      const { data, error } = await supabase.functions.invoke("send-scheduled-conference-report", {
        body: { subscription_id: id, force: true },
      });
      if (error) throw error;
      const result = (data as any)?.results?.[0];
      if (result?.ok) toast({ title: "Report sent" });
      else toast({ title: "Send failed", description: result?.error || "Unknown error", variant: "destructive" });
      load();
    } catch (e: any) {
      toast({ title: "Send failed", description: e.message, variant: "destructive" });
    } finally {
      setTestingId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Scheduled Report Emails</CardTitle>
        <p className="text-sm text-muted-foreground">
          Automatically email a registrations Excel report on a schedule. Reports include payment status (Pay Now / Pay Later / Paid / Pending).
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4 border rounded-lg bg-sky-50/30">
          <div>
            <Label>Recipient email *</Label>
            <Input value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} placeholder="reports@church.org" />
          </div>
          <div>
            <Label>Recipient name</Label>
            <Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} placeholder="Optional" />
          </div>
          {isGlobal && (
            <div>
              <Label>Region scope</Label>
              <Select value={form.region || "__all"} onValueChange={(v) => setForm({ ...form, region: v === "__all" ? "" : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All regions</SelectItem>
                  {regions.map(r => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Frequency</Label>
            <Select value={form.frequency} onValueChange={(v: any) => setForm({ ...form, frequency: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.frequency === "weekly" && (
            <div>
              <Label>Day of week</Label>
              <Select value={String(form.day_of_week)} onValueChange={(v) => setForm({ ...form, day_of_week: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Hour (24h)</Label>
            <Select value={String(form.hour_of_day)} onValueChange={(v) => setForm({ ...form, hour_of_day: Number(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{HOURS.map(h => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Timezone</Label>
            <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="America/New_York">Eastern (ET)</SelectItem>
                <SelectItem value="America/Chicago">Central (CT)</SelectItem>
                <SelectItem value="America/Denver">Mountain (MT)</SelectItem>
                <SelectItem value="America/Los_Angeles">Pacific (PT)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 lg:col-span-3 flex justify-end">
            <Button onClick={add} disabled={saving} className="bg-gradient-to-r from-sky-600 to-amber-500 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Subscription
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : subs.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No scheduled reports yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Last Sent</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subs.map(s => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <div className="font-medium">{s.recipient_email}</div>
                      {s.recipient_name && <div className="text-xs text-muted-foreground">{s.recipient_name}</div>}
                      {s.last_error && <div className="text-xs text-red-600 mt-1">⚠ {s.last_error}</div>}
                    </TableCell>
                    <TableCell>{s.region || <em className="text-muted-foreground">All</em>}</TableCell>
                    <TableCell className="text-sm">
                      {s.frequency === "weekly" ? `Weekly · ${DAYS[s.day_of_week ?? 0]}` : "Daily"} · {String(s.hour_of_day ?? 0).padStart(2, "0")}:00 {(s.timezone || "").split("/")[1] || s.timezone || ""}
                    </TableCell>
                    <TableCell className="text-xs">{s.last_sent_at ? new Date(s.last_sent_at).toLocaleString() : "—"}</TableCell>
                    <TableCell><Switch checked={s.is_active} onCheckedChange={(c) => toggle(s.id, c)} /></TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button size="sm" variant="outline" onClick={() => sendNow(s.id)} disabled={testingId === s.id}>
                        {testingId === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(s.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ScheduledReportsManager;
